import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { toast } from "react-toastify";
import { fnUrl } from "../../../utils/fnUrl";
import { db } from "../utils/firebase";
import { LOCALES, type Locale } from "../utils/locale";

interface LinkItem {
  type: string;
  title: string | Record<string, string>;
  url: string;
}

/** Normalize title to locale object */
function toLocaleTitle(title: string | Record<string, string>): Record<string, string> {
  if (typeof title === "string") return { en: title };
  return title || { en: "" };
}

/** Get locale value from title */
function getLocale(title: string | Record<string, string>, locale: Locale): string {
  if (typeof title === "string") return locale === "en" ? title : "";
  return (title && title[locale]) || "";
}

const AppSettings: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();
  const [legal, setLegal] = useState<LinkItem[]>([]);
  const [about, setAbout] = useState<LinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState<"legal" | "about" | null>(null);
  const [translateProgress, setTranslateProgress] = useState(0);
  const [translateTotal, setTranslateTotal] = useState(0);

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    Promise.all([
      getDoc(doc(db, "content", project, "_config", "legal")),
      getDoc(doc(db, "content", project, "_config", "about")),
    ])
      .then(([legalSnap, aboutSnap]) => {
        setLegal(legalSnap.exists() ? legalSnap.data().items || [] : []);
        setAbout(aboutSnap.exists() ? aboutSnap.data().items || [] : []);
      })
      .catch((err) => console.error("[AppSettings] Load error:", err))
      .finally(() => setLoading(false));
  }, [project]);

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      // Normalize all titles to locale objects before saving
      const normLegal = legal.map((item) => ({ ...item, title: toLocaleTitle(item.title) }));
      const normAbout = about.map((item) => ({ ...item, title: toLocaleTitle(item.title) }));

      await Promise.all([
        setDoc(doc(db, "content", project, "_config", "legal"), {
          items: normLegal,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }, { merge: true }),
        setDoc(doc(db, "content", project, "_config", "about"), {
          items: normAbout,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }, { merge: true }),
      ]);
      setLegal(normLegal);
      setAbout(normAbout);

      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType: "app_settings",
        docTitle: "App Settings",
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[AppSettings] Save error:", err);
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const handleTranslate = async (section: "legal" | "about") => {
    if (!project || !firebaseUser) return;
    const items = section === "legal" ? legal : about;
    const textsToTranslate = items
      .map((item, i) => ({ i, en: typeof item.title === "string" ? item.title : item.title?.en || "" }))
      .filter((t) => t.en.trim());
    if (textsToTranslate.length === 0) { toast.info("Nothing to translate"); return; }

    setTranslating(section);
    setTranslateProgress(0);
    setTranslateTotal(textsToTranslate.length);
    try {
      const idToken = await firebaseUser.getIdToken();
      const url = fnUrl("translateContent");
      let completed = 0;

      const results: { i: number; locales: Record<string, string> }[] = [];
      const MAX_CONCURRENT = 5;
      for (let c = 0; c < textsToTranslate.length; c += MAX_CONCURRENT) {
        const chunk = textsToTranslate.slice(c, c + MAX_CONCURRENT);
        await Promise.all(
          chunk.map(async ({ i, en }) => {
            const resp = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${idToken}`,
              },
              body: JSON.stringify({ key: `${section}.${i}.title`, text: en }),
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            results.push({ i, locales: data.locales as Record<string, string> });
            completed++;
            setTranslateProgress(completed);
          })
        );
      }

      // Auto-save after translation
      const updatedItems = (section === "legal" ? legal : about).map((item, idx) => {
        const result = results.find((r) => r.i === idx);
        if (!result) return { ...item, title: toLocaleTitle(item.title) };
        const t = toLocaleTitle(item.title);
        return { ...item, title: { ...t, ...result.locales } };
      });
      await setDoc(doc(db, "content", project, "_config", section), {
        items: updatedItems,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || "unknown",
      }, { merge: true });
      const setter = section === "legal" ? setLegal : setAbout;
      setter(updatedItems);

      toast.success(`Translated & saved ${section} titles`);
    } catch (err: any) {
      console.error("[AppSettings] Translate error:", err);
      toast.error(`Translation failed: ${err.message}`);
    }
    setTranslating(null);
  };

  const updateItem = (
    section: "legal" | "about",
    index: number,
    field: string,
    value: string,
    locale?: Locale
  ) => {
    const setter = section === "legal" ? setLegal : setAbout;
    setter((prev) => {
      const next = [...prev];
      const item = { ...next[index] };
      if (field === "title" && locale) {
        const t = toLocaleTitle(item.title);
        item.title = { ...t, [locale]: value };
      } else {
        (item as any)[field] = value;
      }
      next[index] = item;
      return next;
    });

  };

  const addItem = (section: "legal" | "about") => {
    const setter = section === "legal" ? setLegal : setAbout;
    setter((prev) => [...prev, { type: "", title: { en: "" }, url: "" }]);

  };

  const removeItem = (section: "legal" | "about", index: number) => {
    const setter = section === "legal" ? setLegal : setAbout;
    setter((prev) => prev.filter((_, i) => i !== index));

  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="general">
      <div className="general__header">
        <h2 className="general__title">App Settings</h2>
        <div className="general__actions">
          <button className="editor__save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <LinkSection
        title="Legal Links"
        items={legal}
        section="legal"
        translating={translating === "legal"}
        onUpdate={updateItem}
        onAdd={addItem}
        onRemove={removeItem}
        onTranslate={() => handleTranslate("legal")}
      />

      <LinkSection
        title="About Links"
        items={about}
        section="about"
        translating={translating === "about"}
        onUpdate={updateItem}
        onAdd={addItem}
        onRemove={removeItem}
        onTranslate={() => handleTranslate("about")}
      />

      {translating && (
        <div className="translate-overlay">
          <div className="translate-overlay__card">
            <div className="translate-overlay__icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/>
                <path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
              </svg>
            </div>
            <p className="translate-overlay__counter">{translateProgress} / {translateTotal}</p>
            <div className="translate-overlay__track">
              <div
                className="translate-overlay__fill"
                style={{ width: `${translateTotal ? (translateProgress / translateTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function LinkSection({
  title,
  items,
  section,
  translating,
  onUpdate,
  onAdd,
  onRemove,
  onTranslate,
}: {
  title: string;
  items: LinkItem[];
  section: "legal" | "about";
  translating: boolean;
  onUpdate: (section: "legal" | "about", index: number, field: string, value: string, locale?: Locale) => void;
  onAdd: (section: "legal" | "about") => void;
  onRemove: (section: "legal" | "about", index: number) => void;
  onTranslate: () => void;
}) {
  return (
    <div className="appsettings__section">
      <div className="appsettings__section-header">
        <h3 className="general__section-title">{title}</h3>
        <button
          className="appsettings__translate-btn"
          onClick={onTranslate}
          disabled={translating || items.length === 0}
        >
          {translating ? "Translating..." : "Translate"}
        </button>
      </div>
      {items.length === 0 && (
        <div className="general__empty" style={{ padding: "var(--space-lg)" }}>
          No {title.toLowerCase()} defined.
        </div>
      )}
      {items.map((item, i) => (
        <div key={i} className="appsettings__item">
          <div className="appsettings__item-top">
            <div className="appsettings__field">
              <label>Type</label>
              <input
                className="block-input block-input--small"
                value={item.type}
                onChange={(e) => onUpdate(section, i, "type", e.target.value)}
                placeholder="privacy, terms, legal..."
              />
            </div>
            <div className="appsettings__field appsettings__field--url">
              <label>URL</label>
              <input
                className="block-input"
                value={item.url}
                onChange={(e) => onUpdate(section, i, "url", e.target.value)}
                placeholder="https://..."
              />
            </div>
            <button
              className="general__remove-field"
              onClick={() => onRemove(section, i)}
              title="Remove"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="appsettings__locales">
            {LOCALES.map((locale) => (
              <div key={locale} className="appsettings__locale-field">
                <span className="appsettings__locale-tag">{locale}</span>
                <input
                  className="block-input"
                  value={getLocale(item.title, locale)}
                  onChange={(e) => onUpdate(section, i, "title", e.target.value, locale)}
                  placeholder={locale === "en" ? "Title (English)" : `Title (${locale})`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button className="general__add-deploy-field" onClick={() => onAdd(section)}>
        + Add link
      </button>
    </div>
  );
}

export default AppSettings;
